import * as Phaser from 'phaser';
import { REFERENCE_WIDTH, REFERENCE_HEIGHT } from '../config/game.config';
import { MaterialToast, materialDisplayName } from '../ui/MaterialToast';
import { RitualSlotHUD } from '../ui/RitualSlotHUD';
import { computeEggGlow, type EggGlowParams } from '../ui/petEggGlow';
import {
  computePlotCell,
  cellCenter,
  GARDEN_LAYOUT,
  type PlotCellState,
  type GrowthStage,
} from '../garden/gardenPlotLogic';
import { ItemType, type JournalSnapshot } from '@game/shared';
import type { JournalSceneDeps } from './JournalScene';

export const SHOWCASE_SCENE_KEY = 'ShowcaseScene' as const;

const BG_KEY = 'showcase-bg';
const BG_PATH = 'assets/scenes/showcase/bg-full.png';

const CANVAS_W = 1344;
const CANVAS_H = 768;

// ── Depth layers ─────────────────────────────────────────────────────
const DEPTH_BG = 0;
const DEPTH_MID = 2;
const DEPTH_FURNITURE = 3;
const DEPTH_PLAYER = 10;
const DEPTH_LABEL = 20;

// ── Ghost player constants ──────────────────────────────────────────
const GHOST_ALPHA_BASE = 0.3;
const GHOST_ALPHA_AMPLITUDE = 0.1;
const GHOST_ALPHA_SPEED = 0.003;

// ── Gather node respawn VFX colors ──────────────────────────────────
const RESPAWN_MOTE_COLORS = [0xc4825a, 0xdaa520, 0xe8c44a, 0xd4a860];

// ── Garden visual colors ────────────────────────────────────────────
const SOIL_COLOR = 0x6b4226;
const SPROUT_STEM_COLOR = 0x3d8b37;
const SAPLING_TRUNK_COLOR = 0x5c3d1e;
const SAPLING_LEAF_COLOR = 0x4aa84a;
const BLOOM_PETAL_COLOR = 0xe88bc4;
const BLOOM_CENTER_COLOR = 0xf5d742;
const GARDEN_BG_COLOR = 0x3d2b18;
const GARDEN_BORDER_COLOR = 0x5c4022;

const FONT = "'Almendra', serif";
const INK = '#eae0c8';

export class ShowcaseScene extends Phaser.Scene {
  private playerSprite!: Phaser.GameObjects.Rectangle;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private escKey!: Phaser.Input.Keyboard.Key;

  private ghostSprites: Phaser.GameObjects.Rectangle[] = [];
  private materialToast!: MaterialToast;
  private gatherKey!: Phaser.Input.Keyboard.Key;
  private gatherNodes: { sprite: Phaser.GameObjects.Arc; depleted: boolean; depletedAt: number }[] = [];
  private ritualSlotHUD!: RitualSlotHUD;

  private eggSprite!: Phaser.GameObjects.Ellipse;
  private eggGlowGfx!: Phaser.GameObjects.Graphics;
  private eggTooltip!: Phaser.GameObjects.Text;
  private eggBondingSeconds = 0;
  private eggStageKey!: Phaser.Input.Keyboard.Key;

  private gardenGraphics!: Phaser.GameObjects.Graphics;
  private fakeSeedRecords: { id: string; plantedAt: number; readyAt: number }[] = [];

  private faintKey!: Phaser.Input.Keyboard.Key;
  private journalKey!: Phaser.Input.Keyboard.Key;

  constructor() {
    super({ key: SHOWCASE_SCENE_KEY });
  }

  preload(): void {
    if (!this.textures.exists(BG_KEY)) {
      this.load.image(BG_KEY, BG_PATH);
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1a1a2e');

    const bg = this.add.image(REFERENCE_WIDTH / 2, REFERENCE_HEIGHT / 2, BG_KEY);
    bg.setDisplaySize(REFERENCE_WIDTH, REFERENCE_HEIGHT);
    bg.setDepth(DEPTH_BG);

    this.playerSprite = this.add.rectangle(REFERENCE_WIDTH / 2, REFERENCE_HEIGHT / 2 + 40, 16, 24, 0x4488ff);
    this.playerSprite.setDepth(DEPTH_PLAYER);

    this.createGhostPlayers();
    this.materialToast = new MaterialToast(this);
    this.createGatherNodes();
    this.ritualSlotHUD = new RitualSlotHUD(this);
    this.createPetEgg();
    this.createGarden();

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.gatherKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.faintKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.F);
    this.journalKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.M);
    this.eggStageKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.B);

    const title = this.add.text(REFERENCE_WIDTH / 2, 12, 'Sprint 1 Showcase Room', {
      fontSize: '14px', fontFamily: FONT, color: INK,
    });
    title.setOrigin(0.5, 0).setDepth(DEPTH_LABEL).setAlpha(0.7);

    const help = [
      'Arrows: move  |  E: gather nearby node  |  B: advance egg stage',
      'F: trigger faint cinematic  |  M: journal (whispers + recipes)',
      '1-4: activate ritual slots  |  ESC: back to title',
    ].join('\n');
    const helpText = this.add.text(REFERENCE_WIDTH / 2, REFERENCE_HEIGHT - 10, help, {
      fontSize: '9px', fontFamily: FONT, color: INK, align: 'center',
    });
    helpText.setOrigin(0.5, 1).setDepth(DEPTH_LABEL).setAlpha(0.5);

    this.game.registry.set('recipes.discoveredIds', new Set(['recipe-herb-soup', 'recipe-stone-bread']));
    this.game.registry.set('player.vocation', 'fool');

    this.cameras.main.setAlpha(0);
    this.tweens.add({ targets: this.cameras.main, alpha: 1, duration: 500, ease: 'Sine.easeInOut' });
  }

  update(time: number, delta: number): void {
    const speed = 120;
    let vx = 0, vy = 0;
    if (this.cursors.left.isDown) vx = -speed;
    else if (this.cursors.right.isDown) vx = speed;
    if (this.cursors.up.isDown) vy = -speed;
    else if (this.cursors.down.isDown) vy = speed;

    const dt = delta / 1000;
    this.playerSprite.x = Phaser.Math.Clamp(this.playerSprite.x + vx * dt, 30, REFERENCE_WIDTH - 30);
    this.playerSprite.y = Phaser.Math.Clamp(this.playerSprite.y + vy * dt, 30, REFERENCE_HEIGHT - 30);

    if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
      this.cleanup();
      this.scene.start('TitleScene');
      return;
    }

    const now = time;
    const ghostAlpha = GHOST_ALPHA_BASE + GHOST_ALPHA_AMPLITUDE * Math.sin(now * GHOST_ALPHA_SPEED);
    for (const ghost of this.ghostSprites) {
      ghost.setAlpha(ghostAlpha);
      const data = ghost.getData('wander') as { baseX: number; baseY: number; phase: number };
      ghost.x = data.baseX + Math.sin(now * 0.0005 + data.phase) * 20;
      ghost.y = data.baseY + Math.cos(now * 0.0003 + data.phase) * 10;
    }

    if (Phaser.Input.Keyboard.JustDown(this.gatherKey)) {
      const nearNode = this.findNearGatherNode();
      if (nearNode >= 0) {
        this.depleteGatherNode(nearNode);
      }
    }

    for (const node of this.gatherNodes) {
      if (node.depleted && time - node.depletedAt > 4000) {
        this.respawnGatherNode(node);
      }
    }

    this.ritualSlotHUD.update();

    if (Phaser.Input.Keyboard.JustDown(this.eggStageKey)) {
      this.eggBondingSeconds += 3600;
      if (this.eggBondingSeconds > 100000) this.eggBondingSeconds = 0;
    }
    this.updateEggGlow(time);

    this.updateGarden();

    if (Phaser.Input.Keyboard.JustDown(this.faintKey)) {
      this.scene.launch('FaintOverlayScene', {
        faintEvent: { weakenedUntil: Date.now() + 30000 },
      });
    }

    if (Phaser.Input.Keyboard.JustDown(this.journalKey)) {
      if (this.scene.isActive('JournalScene')) {
        this.scene.stop('JournalScene');
        this.scene.resume(SHOWCASE_SCENE_KEY);
      } else {
        this.scene.pause(SHOWCASE_SCENE_KEY);
        const deps: JournalSceneDeps = {
          sendMessage: () => {},
          dismiss: () => {
            this.scene.stop('JournalScene');
            this.scene.resume(SHOWCASE_SCENE_KEY);
          },
          initialSnapshot: this.buildDemoSnapshot(),
        };
        this.scene.launch('JournalScene', deps);
      }
    }
  }

  private createGhostPlayers(): void {
    const positions = [
      { x: 160, y: 200 },
      { x: 700, y: 180 },
      { x: 450, y: 350 },
    ];
    const names = ['Wanderer', 'Pilgrim', 'Dreamer'];

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const ghost = this.add.rectangle(pos.x, pos.y, 14, 22, 0x88aacc);
      ghost.setDepth(DEPTH_PLAYER - 1);
      ghost.setAlpha(GHOST_ALPHA_BASE);
      ghost.setData('wander', { baseX: pos.x, baseY: pos.y, phase: i * 2.1 });
      this.ghostSprites.push(ghost);

      const label = this.add.text(pos.x, pos.y - 18, names[i], {
        fontSize: '8px', fontFamily: FONT, color: INK,
      });
      label.setOrigin(0.5, 1).setDepth(DEPTH_PLAYER - 1).setAlpha(GHOST_ALPHA_BASE);
      this.tweens.add({
        targets: label,
        alpha: { from: GHOST_ALPHA_BASE - 0.05, to: GHOST_ALPHA_BASE + GHOST_ALPHA_AMPLITUDE },
        duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }
  }

  private createGatherNodes(): void {
    const positions = [
      { x: 120, y: 320, label: 'Wood' },
      { x: 820, y: 300, label: 'Stone' },
      { x: 200, y: 420, label: 'Thatch' },
    ];
    for (const pos of positions) {
      const sprite = this.add.circle(pos.x, pos.y, 12, 0x4aa84a, 0.8);
      sprite.setDepth(DEPTH_FURNITURE);

      const nodeLabel = this.add.text(pos.x, pos.y - 18, pos.label, {
        fontSize: '9px', fontFamily: FONT, color: INK,
      });
      nodeLabel.setOrigin(0.5, 1).setDepth(DEPTH_FURNITURE + 1).setAlpha(0.6);

      this.gatherNodes.push({ sprite, depleted: false, depletedAt: 0 });
    }
  }

  private findNearGatherNode(): number {
    const px = this.playerSprite.x;
    const py = this.playerSprite.y;
    for (let i = 0; i < this.gatherNodes.length; i++) {
      const node = this.gatherNodes[i];
      if (node.depleted) continue;
      if (Math.abs(px - node.sprite.x) < 40 && Math.abs(py - node.sprite.y) < 40) {
        return i;
      }
    }
    return -1;
  }

  private depleteGatherNode(index: number): void {
    const node = this.gatherNodes[index];
    node.depleted = true;
    node.depletedAt = this.time.now;
    node.sprite.setAlpha(0.3);

    const materials: ItemType[] = [ItemType.MaterialWood, ItemType.MaterialStone, ItemType.MaterialThatch];
    const mat = materials[index % materials.length];
    const qty = 1 + Math.floor(Math.random() * 4);
    this.materialToast.show(qty, materialDisplayName(mat), node.sprite.x, node.sprite.y - 20);
  }

  private respawnGatherNode(node: { sprite: Phaser.GameObjects.Arc; depleted: boolean; depletedAt: number }): void {
    node.depleted = false;
    node.depletedAt = 0;

    const cx = node.sprite.x;
    const cy = node.sprite.y;
    const moteCount = 8 + Math.floor(Math.random() * 5);
    for (let i = 0; i < moteCount; i++) {
      const color = RESPAWN_MOTE_COLORS[Math.floor(Math.random() * RESPAWN_MOTE_COLORS.length)];
      const mote = this.add.circle(cx, cy, 1.5 + Math.random(), color, 0.9);
      mote.setDepth(DEPTH_FURNITURE + 2);
      const angle = (i / moteCount) * Math.PI * 2;
      const dist = 15 + Math.random() * 15;
      this.tweens.add({
        targets: mote,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist - 10,
        alpha: 0, scale: 0.3,
        duration: 1200 + Math.random() * 800,
        ease: 'Sine.easeOut',
        onComplete: () => mote.destroy(),
      });
    }

    this.tweens.add({
      targets: node.sprite,
      alpha: 0.8,
      duration: 2000,
      ease: 'Sine.easeInOut',
    });
  }

  private createPetEgg(): void {
    const eggX = REFERENCE_WIDTH / 2 + 42;
    const eggY = REFERENCE_HEIGHT / 2 - 56;

    this.eggGlowGfx = this.add.graphics().setDepth(DEPTH_MID);
    this.eggSprite = this.add.ellipse(eggX, eggY, 10, 14, 0xf5f0e1).setDepth(DEPTH_FURNITURE);
    this.eggTooltip = this.add.text(eggX, eggY - 24, '', {
      fontSize: '9px', fontFamily: FONT, color: INK,
      backgroundColor: '#1a1a2e', padding: { x: 4, y: 2 },
    });
    this.eggTooltip.setOrigin(0.5, 1).setDepth(DEPTH_LABEL).setAlpha(0.85).setVisible(false);
    this.eggBondingSeconds = 3600;
  }

  private updateEggGlow(time: number): void {
    const params = computeEggGlow(this.eggBondingSeconds, `Stage ${computeEggGlow(this.eggBondingSeconds, '')?.stage ?? 0}`);
    this.eggGlowGfx.clear();
    if (!params) {
      this.eggSprite.setVisible(false);
      this.eggTooltip.setVisible(false);
      return;
    }
    this.eggSprite.setVisible(true);

    const baseX = REFERENCE_WIDTH / 2 + 42;
    const baseY = REFERENCE_HEIGHT / 2 - 56;

    const pulseT = Math.sin(time * 0.001 * params.pulseHz * Math.PI * 2);
    const glowAlpha = 0.35 + 0.20 * pulseT;
    this.eggGlowGfx.fillStyle(params.glowColor, glowAlpha);
    this.eggGlowGfx.fillCircle(baseX, baseY, 18);

    if (params.rocking) {
      this.eggSprite.x = baseX + Math.sin(time * 0.001 * 0.8 * Math.PI * 2) * 3;
    } else {
      this.eggSprite.x = baseX;
    }

    const near = Math.abs(this.playerSprite.x - baseX) < 30 && Math.abs(this.playerSprite.y - baseY) < 24;
    if (near) {
      this.eggTooltip.setText(`Egg stage ${params.stage}/6 [B] advance`);
      this.eggTooltip.setVisible(true);
      this.eggTooltip.x = this.eggSprite.x;
    } else {
      this.eggTooltip.setVisible(false);
    }
  }

  private createGarden(): void {
    this.gardenGraphics = this.add.graphics().setDepth(DEPTH_MID);

    const now = Date.now();
    const growthDuration = 60000;
    this.fakeSeedRecords = [
      { id: 'seed-1', plantedAt: now - growthDuration, readyAt: now },
      { id: 'seed-2', plantedAt: now - growthDuration * 0.6, readyAt: now + growthDuration * 0.4 },
      { id: 'seed-3', plantedAt: now - growthDuration * 0.3, readyAt: now + growthDuration * 0.7 },
      { id: 'seed-4', plantedAt: now, readyAt: now + growthDuration },
    ];

    this.add.text(
      GARDEN_LAYOUT.originX + 60, GARDEN_LAYOUT.originY - 14,
      'Garden', { fontSize: '10px', fontFamily: FONT, color: INK },
    ).setOrigin(0.5, 0.5).setDepth(DEPTH_FURNITURE);
  }

  private updateGarden(): void {
    const now = Date.now();
    const g = this.gardenGraphics;
    g.clear();

    const totalW = GARDEN_LAYOUT.cols * (GARDEN_LAYOUT.cellW + GARDEN_LAYOUT.gapX) - GARDEN_LAYOUT.gapX + 16;
    const totalH = GARDEN_LAYOUT.rows * (GARDEN_LAYOUT.cellH + GARDEN_LAYOUT.gapY) - GARDEN_LAYOUT.gapY + 16;
    g.fillStyle(GARDEN_BG_COLOR, 0.45);
    g.fillRoundedRect(GARDEN_LAYOUT.originX - 8, GARDEN_LAYOUT.originY - 8, totalW, totalH, 6);
    g.lineStyle(1, GARDEN_BORDER_COLOR, 0.6);
    g.strokeRoundedRect(GARDEN_LAYOUT.originX - 8, GARDEN_LAYOUT.originY - 8, totalW, totalH, 6);

    for (let i = 0; i < this.fakeSeedRecords.length; i++) {
      const seed = this.fakeSeedRecords[i];
      const cell = computePlotCell(seed.id, seed.plantedAt, seed.readyAt, now);
      const center = cellCenter(i);
      this.drawPlotCell(g, center.x, center.y, cell.stage);
    }

    const maxSlots = GARDEN_LAYOUT.cols * GARDEN_LAYOUT.rows;
    for (let i = this.fakeSeedRecords.length; i < maxSlots; i++) {
      const center = cellCenter(i);
      g.lineStyle(1, SOIL_COLOR, 0.3);
      g.strokeRect(center.x - GARDEN_LAYOUT.cellW / 2, center.y - GARDEN_LAYOUT.cellH / 2,
        GARDEN_LAYOUT.cellW, GARDEN_LAYOUT.cellH);
    }
  }

  private drawPlotCell(g: Phaser.GameObjects.Graphics, cx: number, cy: number, stage: GrowthStage): void {
    const halfW = GARDEN_LAYOUT.cellW / 2;
    const halfH = GARDEN_LAYOUT.cellH / 2;
    switch (stage) {
      case 'soil':
        g.fillStyle(SOIL_COLOR, 0.8);
        g.fillEllipse(cx, cy + 4, halfW * 1.4, halfH * 0.6);
        break;
      case 'sprout':
        g.fillStyle(SOIL_COLOR, 0.6);
        g.fillEllipse(cx, cy + 8, halfW * 1.2, halfH * 0.4);
        g.lineStyle(2, SPROUT_STEM_COLOR, 1);
        g.lineBetween(cx, cy + 6, cx, cy - 4);
        g.fillStyle(SPROUT_STEM_COLOR, 0.9);
        g.fillTriangle(cx, cy - 2, cx + 5, cy + 1, cx + 1, cy + 3);
        break;
      case 'sapling':
        g.fillStyle(SOIL_COLOR, 0.5);
        g.fillEllipse(cx, cy + 10, halfW * 1.0, halfH * 0.3);
        g.lineStyle(3, SAPLING_TRUNK_COLOR, 1);
        g.lineBetween(cx, cy + 8, cx, cy - 6);
        g.fillStyle(SAPLING_LEAF_COLOR, 0.8);
        g.fillCircle(cx - 6, cy - 7, 4);
        g.fillCircle(cx + 6, cy - 9, 4);
        g.fillCircle(cx, cy - 8, 5);
        break;
      case 'bloom': {
        g.fillStyle(SOIL_COLOR, 0.4);
        g.fillEllipse(cx, cy + 10, halfW * 0.8, halfH * 0.25);
        g.lineStyle(2, SPROUT_STEM_COLOR, 1);
        g.lineBetween(cx, cy + 8, cx, cy - 8);
        g.fillStyle(SAPLING_LEAF_COLOR, 0.7);
        g.fillEllipse(cx - 4, cy + 2, 4, 2);
        g.fillEllipse(cx + 4, cy - 1, 4, 2);
        const flowerCy = cy - 10;
        for (let p = 0; p < 5; p++) {
          const angle = (p / 5) * Math.PI * 2 - Math.PI / 2;
          g.fillStyle(BLOOM_PETAL_COLOR, 0.85);
          g.fillCircle(cx + Math.cos(angle) * 5, flowerCy + Math.sin(angle) * 5, 4);
        }
        g.fillStyle(BLOOM_CENTER_COLOR, 1);
        g.fillCircle(cx, flowerCy, 3);
        break;
      }
    }
  }

  private buildDemoSnapshot(): JournalSnapshot {
    const now = Date.now();
    return {
      characterId: 'showcase-demo',
      whispers: [
        {
          id: 'w-demo-1', source: 'dreamed' as never, realm: 'verdana' as never,
          body: 'The old grove remembers a song that no one sings anymore. Its roots hum with forgotten warmth.',
          unlockedAt: now - 86400000,
        },
        {
          id: 'w-demo-2', source: 'dreamed' as never, realm: 'hill' as never,
          body: 'A lantern lit by no hand glows between the standing stones when the mist is thickest.',
          unlockedAt: now - 43200000,
        },
        {
          id: 'w-demo-3', source: 'dreamed' as never, realm: 'frostmere' as never,
          body: 'Beneath the frozen falls, something stirs — not ice, not water, but a patience older than both.',
          unlockedAt: now - 3600000,
        },
      ],
      rumors: [
        {
          id: 'r-demo-1', name: 'The Handwriting on the Crane',
          hint: 'A shrine lives between two hills where the stream once ran.',
          unlockedByWhisperIds: ['w-demo-1', 'w-demo-2'],
          unlockedAt: now - 7200000,
        },
      ],
      relics: [],
      readAtByWhisperId: { 'w-demo-1': now - 50000 },
      unreadCount: 2,
      discoveredRecipeIds: ['recipe-herb-soup', 'recipe-stone-bread'],
      vocationKey: 'fool',
    };
  }

  private cleanup(): void {
    this.materialToast.destroy();
    this.ritualSlotHUD.destroy();
  }

  shutdown(): void {
    this.cleanup();
  }
}
